import { Col, Row, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

import { MonthlyAttendanceStatsDto } from '../../../../../../../../types/dto/statistics.dto';

interface Props {
  selectedMonthData: MonthlyAttendanceStatsDto;
}

const COLORS = ['var(--green)', 'var(--red)'];

const MonthDetails = ({ selectedMonthData }: Props) => {
  const { t } = useTranslation('user');

  const columns: ColumnsType<(typeof selectedMonthData.events)[0]> = [
    {
      title: t('attendance_stats.detailed.events_table.date'),
      dataIndex: 'dateStart',
      key: 'date',
      render: (_, record) => (
        <>
          <p className='m-0'>{`${dayjs(record.dateStart).format(
            'DD.MM.YYYY.'
          )}`}</p>
          <p className='m-0 text-gray' style={{ fontSize: 12 }}>{`${dayjs(
            record.dateStart
          ).format('HH:mm')} - ${dayjs(record.dateEnd).format('HH:mm')}`}</p>
        </>
      ),
    },
    {
      title: t('attendance_stats.detailed.events_table.check_ins'),
      dataIndex: 'eventCheckInCount',
      key: 'checkins',
      align: 'center',
      render: (value, record) => `${value}/${record.expectedCount}`,
    },

    {
      title: t('attendance_stats.detailed.events_table.attendance'),
      dataIndex: 'eventAttendance',
      key: 'attendance',
      align: 'center',
      render: (value: number) => `${value.toFixed(2)}%`,
    },
  ];

  const pieData = [
    {
      name: t('attendance_stats.attended'),
      value: selectedMonthData.monthAttendance,
    },
    {
      name: t('attendance_stats.not_attended'),
      value: 100 - selectedMonthData.monthAttendance,
    },
  ];

  return (
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      <Col xs={24} sm={16}>
        <h4>{t('attendance_stats.detailed.events_table.events')}</h4>
        <Table
          dataSource={selectedMonthData.events}
          columns={columns}
          rowKey='eventId'
          size='small'
          pagination={{ hideOnSinglePage: true, pageSize: 5 }}
        />
      </Col>

      <Col xs={24} sm={8}>
        <h4 style={{ textAlign: 'center' }}>
          {t('attendance_stats.detailed.monthly_pie.title')}
        </h4>
        <ResponsiveContainer width={'100%'} height={300}>
          <PieChart width={250}>
            <Pie
              data={pieData}
              dataKey='value'
              nameKey='name'
              cx='50%'
              cy='50%'
              outerRadius={80}
              label={({ percent }) => `${(percent! * 100).toFixed(2)}%`}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Col>
    </Row>
  );
};

export default MonthDetails;
